import React from "react";
import { Link } from "react-router-dom";
import { Send, Search, Truck, Building2, CheckCircle2, ArrowRight, Package } from "lucide-react";
import { Button } from "../../components/ui/button";
import { IdeaLogoBadge } from "../../components/IdeaLogoBadge";

const STEPS = [
  { icon: Send, title: "Submit Request", desc: "Share your order, product and the issue with photos & video." },
  { icon: Search, title: "IDEA Reviews", desc: "Our team verifies your claim and warranty eligibility." },
  { icon: Truck, title: "Send Product", desc: "Once approved, ship the product to IDEA for inspection." },
  { icon: Building2, title: "Supplier Processing", desc: "We escalate to the supplier for credit or replacement." },
  { icon: CheckCircle2, title: "Resolution", desc: "Replacement or credit is processed and returned to you." },
];

function PublicHeader() {
  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/warranty" className="flex items-center gap-3">
          <IdeaLogoBadge />
        </Link>
        <Link to="/warranty/track">
          <Button variant="outline" data-testid="track-claim-link" className="gap-2">
            <Search className="h-4 w-4" /> Track Claim
          </Button>
        </Link>
      </div>
    </header>
  );
}

export default function WarrantyPortal() {
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="animate-fade-up">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              <Package className="h-3.5 w-3.5" /> Product Warranty & RMA
            </div>
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Warranty Claim
            </h1>
            <p className="mt-4 max-w-md text-lg text-slate-600">
              Submit and track your product warranty request. Fast, transparent and fully tracked from submission to resolution.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/warranty/claim">
                <Button data-testid="start-warranty-claim-btn" size="lg" className="gap-2 bg-blue-600 text-base hover:bg-blue-700">
                  Start Warranty Claim <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/warranty/track">
                <Button data-testid="track-hero-btn" variant="outline" size="lg" className="gap-2 text-base">
                  <Search className="h-4 w-4" /> Track Existing Claim
                </Button>
              </Link>
            </div>
          </div>
          <div className="animate-fade-up rounded-3xl border border-slate-200 bg-[#0F172A] p-8 shadow-xl" style={{ animationDelay: "0.1s" }}>
            <img
              src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
              alt="Electronics inspection"
              className="mb-6 h-48 w-full rounded-xl object-cover"
            />
            <div className="space-y-3 text-slate-300">
              <div className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-blue-400" /> Secure photo & video evidence upload</div>
              <div className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-blue-400" /> Real-time status tracking</div>
              <div className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-blue-400" /> Credit & replacement management</div>
            </div>
          </div>
        </div>

        {/* Process */}
        <section className="mt-20">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">How it works</h2>
          <p className="mt-2 text-slate-600">A simple five-step process from claim to resolution.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map((s, i) => (
              <div key={s.title} className="animate-fade-up rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" style={{ animationDelay: `${i * 0.06}s` }}>
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="mb-1 text-xs font-bold text-blue-600">STEP {i + 1}</div>
                <h3 className="font-display text-base font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-1 text-sm text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
